import { useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useLocation, useNavigate } from 'react-router-dom'
import { isPast } from 'date-fns'

import { mutations, queries } from '../../graphql'
import { useAuth } from '../../hooks'
import { useGlobalAlert } from '../alert/globalAlert'
import { NotificationDialog } from './notification-dialog'

const CHARITY_INVITE_INVITED = 1
const REFETCH_INCLUDE = [
  'me',
  'myFunds',
  'myFund',
  'myAccessibleFunds',
  'myPendingAdvisorInvitations',
  'myPendingFundClaimInvites',
]

/**
 * Surfaces any pending invite (charity portal or advisor connection) for the
 * logged-in user, regardless of which portal they land in after sign-in, one
 * at a time. Mounted once near the app root - see App.jsx.
 */
export function PendingInviteNotifier() {
  const [{ isLoggedIn, hasShownPendingInvitePopup }, { markPendingInvitePopupShown }] = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const apolloClient = useApolloClient()
  const [, { setGlobalAlert }] = useGlobalAlert()
  const [dismissedKeys, setDismissedKeys] = useState(() => new Set())
  // This component is mounted once at the app root and never unmounts across login/logout,
  // so hasShownPendingInvitePopup must be read reactively (a fresh sign-in resets it - see
  // useAuth.js login()) rather than snapshotted once. Once we've decided to show a dialog,
  // this ref keeps it open even after marking the flag flips hasShownPendingInvitePopup back
  // to true for this same render cycle.
  const hasDecidedToShowRef = useRef(false)

  // dismissedKeys/hasDecidedToShowRef are local to this never-unmounting component, so
  // without this they'd persist across a logout/login cycle - permanently hiding a still-
  // pending invite that was merely dismissed (or accepted-but-not-completed) in a previous
  // session, even on a genuinely fresh sign-in. hasShownPendingInvitePopup flipping to false
  // is the same signal login()/logout() already use to mean "treat this as a new session".
  useEffect(() => {
    if (!hasShownPendingInvitePopup) {
      setDismissedKeys(new Set())
      hasDecidedToShowRef.current = false
    }
  }, [hasShownPendingInvitePopup])

  // The advisor sign-up and accept pages already have their own dedicated messaging/UI for
  // the specific invite in the URL (including the "you already have an account, log in to
  // accept" prompt on the sign-up page) - showing this global popup on top of them would
  // redundantly re-prompt for (and can race with) the exact invite already being handled there.
  //
  // /login itself is also suppressed: LoginForm's onCompleted flips isLoggedIn true and then
  // calls navigate(destination) - there's a render in between where isLoggedIn is already true
  // but the URL is still /login, which is enough to flash this popup before the post-login
  // redirect actually lands. The popup will correctly show on whichever page the login flow
  // redirects to (unless that page is itself suppressed above).
  const shouldSuppressPendingInvitePopup =
    location.pathname.startsWith('/advisor/accept') ||
    location.pathname.startsWith('/sign-up/advisor') ||
    location.pathname.startsWith('/claim-fund') ||
    location.pathname === '/login'

  const { data: meData } = useQuery(queries.user.me, { skip: !isLoggedIn || shouldSuppressPendingInvitePopup })
  const { data: advisorData } = useQuery(queries.onboarding.myPendingAdvisorInvitations, {
    skip: !isLoggedIn || shouldSuppressPendingInvitePopup,
  })
  const { data: fundClaimData } = useQuery(queries.fundClaims.myPendingFundClaimInvites, {
    skip: !isLoggedIn || shouldSuppressPendingInvitePopup,
  })

  const [declineCharityInvite] = useMutation(mutations.charities.declineCharityInvite)
  const [declineAdvisorConnection] = useMutation(mutations.onboarding.declineAdvisorConnection)
  const [declineFundClaimInvite] = useMutation(mutations.fundClaims.declineFundClaimInvite)

  const pendingItems = useMemo(() => {
    const charityUsers = meData?.me?.charityUsers || []
    const charityItems = charityUsers
      .filter((cu) => cu.charityInvite && Number(cu.charityInvite.status) === CHARITY_INVITE_INVITED)
      .map((cu) => ({
        kind: 'charity',
        key: `charity-${cu.charityInvite.id}`,
        charityInviteId: Number(cu.charityInvite.id),
        charityInviteToken: cu.charityInvite.token,
        charityId: cu.charityId,
        userId: cu.userId,
        charityName: cu.charity?.accountName || 'the charity',
      }))

    // A FundProfile(fundType: 'advisor') is pre-provisioned for portal display as soon as
    // an invite is sent, whether or not it's been accepted, so it can't distinguish "pending"
    // from "active" - the RBAC ADVISOR role (assigned only once a connection is accepted, and
    // always scoped to a specific fundId) is the reliable signal for an active advisor portal.
    const userRoles = meData?.me?.userRoles || []
    const hasActiveAdvisorPortal = userRoles.some((ur) => ur.role?.name === 'ADVISOR' && ur.fundId != null)

    const advisorInvites = hasActiveAdvisorPortal ? [] : advisorData?.myPendingAdvisorInvitations || []
    const advisorItems = advisorInvites
      .filter((invite) => !invite.expiresAt || !isPast(new Date(invite.expiresAt)))
      .map((invite) => ({
        kind: 'advisor',
        key: `advisor-${invite.id}`,
        flowId: invite.id,
        donorName: invite.donorName || 'A donor',
      }))

    const fundClaimInvites = fundClaimData?.myPendingFundClaimInvites || []
    const fundClaimItems = fundClaimInvites
      .filter((invite) => !invite.expiresAt || !isPast(new Date(invite.expiresAt)))
      .map((invite) => ({
        kind: 'fund-claim',
        key: `fund-claim-${invite.id}`,
        token: invite.token,
        fundName: invite.fundName,
        advisorName: invite.advisorName || 'An advisor',
        isNewFund: invite.isNewFund,
      }))

    return [...charityItems, ...advisorItems, ...fundClaimItems].filter((item) => !dismissedKeys.has(item.key))
  }, [
    meData?.me?.charityUsers,
    meData?.me?.userRoles,
    advisorData?.myPendingAdvisorInvitations,
    fundClaimData?.myPendingFundClaimInvites,
    dismissedKeys,
  ])

  const current =
    shouldSuppressPendingInvitePopup || (hasShownPendingInvitePopup && !hasDecidedToShowRef.current)
      ? undefined
      : pendingItems[0]

  useEffect(() => {
    if (current) {
      hasDecidedToShowRef.current = true
      markPendingInvitePopupShown()
    }
  }, [current, markPendingInvitePopupShown])

  if (!current) return null

  const dismiss = () => setDismissedKeys((prev) => new Set(prev).add(current.key))

  const refreshAfterAction = async () => {
    await apolloClient.refetchQueries({ include: REFETCH_INCLUDE })
  }

  const handleAccept = () => {
    dismiss()
    if (current.kind === 'charity') {
      navigate(`/charities/${current.charityId}/claim/${current.userId}/${current.charityInviteToken}`)
    } else if (current.kind === 'fund-claim') {
      navigate(`/claim-fund/${current.token}`)
    } else {
      navigate(`/advisor/accept-by-id/${current.flowId}`)
    }
  }

  const handleDecline = () => {
    dismiss()
    let declineMutation
    let declinedWhat
    if (current.kind === 'charity') {
      declineMutation = declineCharityInvite({ variables: { charityInviteId: current.charityInviteId } })
      declinedWhat = `the ${current.charityName} portal`
    } else if (current.kind === 'fund-claim') {
      declineMutation = declineFundClaimInvite({ variables: { token: current.token } })
      declinedWhat = current.fundName || 'the Giving Fund'
    } else {
      declineMutation = declineAdvisorConnection({ variables: { flowId: current.flowId } })
      declinedWhat = current.donorName
    }

    declineMutation
      .then(async () => {
        await refreshAfterAction()
        setGlobalAlert({ message: `Invite from ${declinedWhat} declined.`, severity: 'info' })
      })
      .catch((error) => {
        setGlobalAlert({
          message: 'Something went wrong declining the invite.',
          severity: 'error',
          error: error.message,
        })
      })
  }

  let title
  let message
  if (current.kind === 'charity') {
    title = 'Charity Portal Invitation'
    message = `You've been invited to manage the ${current.charityName} portal. Would you like to accept?`
  } else if (current.kind === 'fund-claim') {
    if (current.isNewFund) {
      title = 'New Giving Fund Invitation'
      message = `${current.advisorName} has set up a new Giving Fund for you. Would you like to accept it?`
    } else {
      title = 'Financial Advisor Access Request'
      message = `${current.advisorName} has requested to advise on your Giving Fund. Would you like to accept?`
    }
  } else {
    title = 'Advisor Connection Request'
    message = `${current.donorName} has invited you to be their advisor. Would you like to accept the connection?`
  }

  return (
    <NotificationDialog
      open
      onClose={dismiss}
      type="alert"
      title={title}
      message={message}
      actions={[
        { label: 'Decline', onClick: handleDecline, color: 'error' },
        { label: 'Accept', onClick: handleAccept, variant: 'contained', color: 'primary', autoFocus: true },
      ]}
    />
  )
}
